import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { useTheme } from '../../context/ThemeContext';
import {
    ChevronDown, Menu, X,
    Users, LayoutDashboard, ShieldPlus, ShieldCheck,
    Store, Notebook, Contact, CalendarCheck, TicketPercent,
    UserRoundCheck, IdCard, Warehouse, Banknote, BarChart3, Building2, Mail,
    Receipt, CalendarDays, History, Sparkles
} from 'lucide-react';

const colors = {
    primary: '#FF2052',
    primaryDark: '#9B004A',
    secondary: '#007FC8',
    secondaryLight: '#00DBB9',
    accentDark: '#EC8400',
};

interface NavItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    color: string;
    links: { href: string; label: string; desc: string; icon?: React.ReactNode }[];
}

const navItems: NavItem[] = [
    {
        id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={16} />, color: colors.secondary,
        links: [
            { href: '/dashboard', label: 'Dashboard Financiero', desc: 'Resumen financiero general' },
            { href: '/dashboard-score', label: 'Dashboard Score', desc: 'Métricas de rendimiento' },
        ],
    },
    {
        id: 'correspondencia', label: 'Correspondencia', icon: <Mail size={16} />, color: colors.secondaryLight,
        links: [
            { href: '/config-correspondencia', label: 'Configuración de correspondencia', desc: 'Planes y condiciones de recepción' },
            { href: '/correspondencias', label: 'Gestión de Correspondencia', desc: 'Panel de paquetes, códigos y entregas' },
            { href: '/config-correspondencia', label: 'Config. Correspondencia', desc: 'Reglas y parámetros de correspondencia' },
        ],
    },
    {
        id: 'Productos', label: 'Productos', icon: <Store size={16} />, color: colors.primary,
        links: [
            { href: '/categoriasproductos', label: 'Categorías productos', desc: 'Gestiona las categorías de los productos' },
            { href: '/productos', label: 'Productos', desc: 'Lista completa de productos' },
        ],
    },
    {
        id: 'servicios', label: 'Servicios', icon: <Store size={16} />, color: colors.primary,
        links: [
            { href: '/categorias', label: 'Categorías', desc: 'Gestiona las categorías de servicios' },
            { href: '/services', label: 'Servicios', desc: 'Lista completa de servicios' },
            { href: '/spaces', label: 'Espacios', desc: 'Administra los espacios disponibles' },
            { href: '/spaces-services', label: 'Espacios de Servicios', desc: 'Relaciona espacios con servicios' },
        ],
    },
    {
        id: 'planes', label: 'Planes', icon: <Notebook size={16} />, color: colors.accentDark,
        links: [
            { href: '/plan', label: 'Planes', desc: 'Ver y gestionar planes' },
            { href: '/priceplan', label: 'Precios de Planes', desc: 'Configura tarifas por plan' },
            { href: '/planconfig', label: 'Configuración de Planes', desc: 'Ajustes avanzados de planes' },
        ],
    },
    {
        id: 'pos', label: 'Punto de venta', icon: <Store size={16} />, color: colors.secondaryLight,
        links: [
            { href: '/pos-list', label: 'Punto de venta', desc: 'Panel principal de reservas', icon: <Store size={15} /> },
            { href: '/reservas', label: 'Ordenes', desc: 'Listado de todas las reservas', icon: <Receipt size={15} /> },
            { href: '/reservas-calendario', label: 'Reservas Calendario', desc: 'Vista de calendario interactivo', icon: <CalendarDays size={15} /> },
            { href: '/sesiones-de-pos', label: 'Sesiones del POS', desc: 'Historial de sesiones POS', icon: <History size={15} /> },
            { href: '/sugerencias-citas', label: 'Citas recomendadas', desc: 'Sugerencias automáticas de citas', icon: <Sparkles size={15} /> },
        ],
    },
    {
        id: 'reports', label: 'Reportes', icon: <BarChart3 size={16} />, color: colors.primary,
        links: [
            { href: '/reportes/reservas', label: 'Reservas', desc: 'Ocupación y estado de reservas' },
            { href: '/reportes/cafeteria', label: 'Ventas de cafetería', desc: 'Ventas, cobros y saldos' },
            { href: '/reportes/cancelaciones', label: 'Cancelaciones de reservas', desc: 'Historial y motivos de cancelación' },
            { href: '/reportes/pagos', label: 'Pagos', desc: 'Ingresos y medios de pago' },
            { href: '/reportes/caja', label: 'Caja', desc: 'Movimientos y cierres de caja' },
            { href: '/reportes/uso', label: 'Uso', desc: 'Uso de espacios y servicios' },
            { href: '/reportes/clientes', label: 'Clientes', desc: 'Actividad y recurrencia de clientes' },
            { href: '/reportes/actividad', label: 'Actividad', desc: 'Auditoría de acciones administrativas' },
            { href: '/reportes/seguridad', label: 'Seguridad', desc: 'Accesos y eventos de seguridad' },
        ],
    },
    {
        id: 'empresa', label: 'Empresa', icon: <Building2 size={16} />, color: colors.accentDark,
        links: [
            { href: '/empresa', label: 'Empresa y Facturación', desc: 'Compañías, SIAT, CUIS, CUFD y facturas' },
        ],
    },
    {
        id: 'sucursales', label: 'Sucursales', icon: <Warehouse size={16} />, color: colors.secondary,
        links: [
            { href: '/sucursales', label: 'Sucursales', desc: 'Gestiona las sedes de la empresa' },
        ],
    },
    {
        id: 'kardex', label: 'Kardex', icon: <IdCard size={16} />, color: colors.primary,
        links: [
            { href: '/kardex-citas', label: 'Kardex de Citas', desc: 'Registro histórico de citas' },
            { href: '/afiliados', label: 'Afiliados & Requisitos', desc: 'Flujo Miro de afiliación y 8h cortesía' },
        ],
    },
    {
        id: 'coupon', label: 'Cupones', icon: <TicketPercent size={16} />, color: colors.accentDark,
        links: [
            { href: '/coupon', label: 'Cupones', desc: 'Crea y administra cupones' },
            { href: '/coupon-used', label: 'Cupones Usados', desc: 'Historial de cupones canjeados' },
        ],
    },
    {
        id: 'system', label: 'Sistema', icon: <UserRoundCheck size={16} />, color: colors.secondaryLight,
        links: [
            { href: '/system', label: 'Sistema', desc: 'Configuración global del sistema' },
        ],
    },
    {
        id: 'users', label: 'Usuarios', icon: <Users size={16} />, color: colors.secondary,
        links: [
            { href: '/usuarios', label: 'Usuarios', desc: 'Listado de usuarios registrados' },
            { href: '/nivelusuarios', label: 'Nivel de Usuarios', desc: 'Categorías y rangos de usuarios' },
            { href: '/reglas-nivel', label: 'Reglas de Nivel', desc: 'Define reglas por nivel de usuario' },
        ],
    },
    {
        id: 'actors-systems', label: 'Actores', icon: <Contact size={16} />, color: colors.primary,
        links: [
            { href: '/clients', label: 'Clientes', desc: 'Base de datos de clientes' },
            { href: '/personnel', label: 'Personal', desc: 'Gestión del equipo interno' },
        ],
    },
    {
        id: 'order', label: 'Órdenes', icon: <IdCard size={16} />, color: colors.accentDark,
        links: [
            {
                href: '/codigos-orden',
                label: 'Código de Orden',
                desc: 'Gestiona códigos de órdenes',
            },
            {
                href: '/codigo-pago-orden',
                label: 'Código de Pagos',
                desc: 'Códigos relacionados a pagos',
            },
            {
                href: '/codigo-sesionpos-orden',
                label: 'Código de Sesión POS',
                desc: 'Códigos de sesiones POS',
            },
        ],
    },
    {
        id: 'pagos', label: 'Pagos', icon: <Banknote size={16} />, color: colors.secondaryLight,
        links: [
            { href: '/pagos', label: 'Todos los Pagos', desc: 'Historial completo de pagos registrados' },
        ]
    },
    {
        id: 'config', label: 'Configuración', icon: <ShieldPlus size={16} />, color: colors.secondary,
        links: [
            { href: '/profile', label: 'Perfil', desc: 'Tu información de perfil' },
            { href: '/permission', label: 'Permisos', desc: 'Roles y permisos del sistema' },
            { href: '/tipos-pago', label: 'Tipos de Pago', desc: 'Métodos de pago aceptados' },
            { href: '/config-pago', label: 'Configuración de Pagos', desc: 'Ajustes de pasarela de pago' },
            { href: '/config-horas-regalo', label: 'Config. Horas de Regalo', desc: 'Horas de cortesía por plan' },
            { href: '/config-correspondencia', label: 'Config. Correspondencia', desc: 'Gestión de correspondencia' },
        ],
    },
    {
        id: 'historial', label: 'Historial', icon: <ShieldCheck size={16} />, color: colors.primary,
        links: [
            { href: '/activity', label: 'Actividades', desc: 'Registro de actividad del sistema' },
            { href: '/security', label: 'Seguridad', desc: 'Eventos y alertas de seguridad' },
        ],
    },
    {
        id: 'landing', label: 'Landing', icon: <Notebook size={16} />, color: colors.accentDark,
        links: [
            { href: '/banner-hero', label: 'Banner & Secciones Landing', desc: 'Hero banner, galería, experiencia y secciones' },
            { href: '/blog-categoria', label: 'Categorías de Blog', desc: 'Gestiona las categorías del blog' },
            { href: '/blog-post', label: 'Publicaciones de Blog', desc: 'Crea y administra publicaciones' },
            { href: '/empresas-confianza', label: 'Empresas de Confianza', desc: 'Gestiona las empresas de confianza en la landing' },
            { href: '/ramas-profesionales', label: 'Ramas Profesionales', desc: 'Gestiona las ramas y sub-ramas del banco' },
            { href: '/eventos-admin', label: 'Eventos & Networking', desc: 'Gestiona eventos, networking y asistentes' },
        ],
    },
];

// ── Dropdown en position:fixed fuera del nav para evitar overflow clip ──
const DropdownMenu = React.forwardRef<HTMLDivElement, {
    item: NavItem;
    triggerRect: DOMRect;
    onClose: () => void;
}>(({ item, triggerRect, onClose }, ref) => {
    const { isDark } = useTheme();
    const top = triggerRect.bottom + 8;
    const left = Math.min(triggerRect.left, window.innerWidth - 244);

    return (
        <div
            ref={ref}
            data-dropdown-content={item.id}
            style={{
                position: 'fixed', top, left,
                minWidth: '230px',
                background: isDark ? '#160a2c' : '#fff',
                borderRadius: '16px',
                border: isDark ? '1.5px solid rgba(193, 52, 139, 0.35)' : '1.5px solid #f1f5f9',
                boxShadow: isDark
                    ? '0 16px 48px rgba(0,0,0,0.7), 0 0 24px rgba(193,52,139,0.2)'
                    : `0 16px 48px rgba(0,0,0,0.09), 0 4px 16px rgba(0,0,0,0.05), 0 0 0 1px ${item.color}08`,
                overflow: 'hidden', zIndex: 9999,
                fontFamily: "'DM Sans', sans-serif",
            }}
        >
            <div style={{ height: '3px', background: `linear-gradient(90deg, ${item.color}, ${item.color}40)` }} />
            <div style={{ padding: '8px' }}>
                {item.links.map(link => (
                    <Link
                        key={link.href}
                        to={link.href}
                        onClick={onClose}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', textDecoration: 'none', color: isDark ? '#cbd5e1' : '#374151', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = isDark ? `${item.color}25` : `${item.color}08`; e.currentTarget.style.transform = 'translateX(3px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}
                    >
                        <div style={{ width: '30px', height: '30px', borderRadius: '9px', flexShrink: 0, background: `${item.color}15`, border: `1.5px solid ${item.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.color }}>
                            {link.icon || item.icon}
                        </div>
                        <div>
                            <div style={{ fontSize: '0.83rem', fontWeight: '700', color: isDark ? '#f8fafc' : '#0f172a', lineHeight: '1.2' }}>{link.label}</div>
                            <div style={{ fontSize: '0.7rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '500', marginTop: '2px' }}>{link.desc}</div>
                        </div>
                        <div style={{ marginLeft: 'auto', fontSize: '0.75rem', color: `${item.color}60` }}>→</div>
                    </Link>
                ))}
            </div>
        </div>
    );
});
DropdownMenu.displayName = 'DropdownMenu';

const Navbar: React.FC = () => {
    const { isDark } = useTheme();
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
    const [fadeLeft, setFadeLeft] = useState(false);
    const [fadeRight, setFadeRight] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    const isItemActive = (item: NavItem) => {
        if (openDropdown === item.id) return true;
        return item.links.some(l => location.pathname === l.href || (l.href !== '/' && location.pathname.startsWith(l.href)));
    };

    useEffect(() => {
        setOpenDropdown(null);
        setTriggerRect(null);
        setMobileOpen(false);
    }, [location.pathname]);

    const checkFade = () => {
        const el = menuRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setFadeLeft(scrollLeft > 4);
        setFadeRight(scrollLeft + clientWidth < scrollWidth - 4);
    };

    useEffect(() => {
        const el = menuRef.current;
        if (!el) return;
        setTimeout(checkFade, 80);
        el.addEventListener('scroll', checkFade);
        window.addEventListener('resize', checkFade);

        // Convierte scroll vertical de rueda de mouse → scroll horizontal
        const onWheel = (e: WheelEvent) => {
            // Solo actuar si hay overflow horizontal real
            if (el.scrollWidth <= el.clientWidth) return;
            e.preventDefault();
            // deltaY (rueda vertical) o deltaX (rueda horizontal en trackpad)
            el.scrollBy({ left: e.deltaY !== 0 ? e.deltaY : e.deltaX, behavior: 'smooth' });
        };
        el.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            el.removeEventListener('scroll', checkFade);
            el.removeEventListener('wheel', onWheel);
            window.removeEventListener('resize', checkFade);
        };
    }, []);

    const handleTriggerClick = (id: string, e: React.MouseEvent<HTMLButtonElement>) => {
        if (openDropdown === id) {
            setOpenDropdown(null);
            setTriggerRect(null);
        } else {
            setTriggerRect(e.currentTarget.getBoundingClientRect());
            setOpenDropdown(id);
        }
    };

    useEffect(() => {
        const handle = (e: MouseEvent) => {
            const t = e.target as HTMLElement;
            if (!t.closest('[data-dropdown-trigger]') && !t.closest('[data-dropdown-content]')) {
                setOpenDropdown(null);
                setTriggerRect(null);
            }
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, []);

    useEffect(() => {
        const handle = () => {
            setScrolled(window.scrollY > 4);
            setOpenDropdown(null);
            setTriggerRect(null);
        };
        window.addEventListener('scroll', handle);
        return () => window.removeEventListener('scroll', handle);
    }, []);

    useEffect(() => {
        const handle = () => { if (window.innerWidth >= 640) setMobileOpen(false); };
        window.addEventListener('resize', handle);
        return () => window.removeEventListener('resize', handle);
    }, []);

    const activeItem = navItems.find(i => i.id === openDropdown) ?? null;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');

                .nav-trigger { transition: all 0.2s cubic-bezier(.16,1,.3,1); }
                .nav-trigger:hover { transform: translateY(-1px); }
                .nav-hamburger { display: none; }
                .nav-desktop-wrap { display: flex; }
                .nav-brand-bar { display: flex; }

                /* Scrollbar invisible — navbar desktop horizontal */
                .adm-scroll::-webkit-scrollbar { display: none; }
                .adm-scroll {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                    overflow-x: auto;
                    overflow-y: visible;
                }

                /* Scrollbar estilizado — menú móvil vertical */
                .adm-mobile-scroll::-webkit-scrollbar { width: 6px; }
                .adm-mobile-scroll::-webkit-scrollbar-track { background: transparent; }
                .adm-mobile-scroll::-webkit-scrollbar-thumb {
                    background-color: rgba(0,127,200,0.3);
                    border-radius: 20px;
                }
                .adm-mobile-scroll::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(0,127,200,0.5);
                }

                @media (max-width: 639px) {
                    .nav-hamburger    { display: flex !important; }
                    .nav-desktop-wrap { display: none  !important; }
                    .nav-brand-bar    { display: none  !important; }
                }
            `}</style>

            <nav style={{
                fontFamily: "'DM Sans', sans-serif",
                background: isDark ? '#110820' : '#fff',
                borderBottom: scrolled || mobileOpen
                    ? (isDark ? '1.5px solid rgba(193, 52, 139, 0.3)' : '1.5px solid #f1f5f9')
                    : (isDark ? '1.5px solid rgba(255, 255, 255, 0.06)' : '1.5px solid transparent'),
                boxShadow: scrolled
                    ? (isDark ? '0 4px 20px rgba(0,0,0,0.6)' : '0 2px 16px rgba(0,0,0,0.05)')
                    : 'none',
                transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
                position: 'sticky', top: '64px', zIndex: 40,
                // clip solo en X para que el overflow-Y no corte los dropdowns del portal
                overflowX: 'clip',
            }}>
                <div style={{ padding: '0 clamp(16px,3vw,32px)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', height: '52px', gap: '4px' }}>

                        {/* Barra de colores */}
                        <div className="nav-brand-bar" style={{ gap: '3px', marginRight: '16px', flexShrink: 0 }}>
                            {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c, i) => (
                                <div key={i} style={{ width: i === 0 ? '14px' : '5px', height: '3px', borderRadius: '3px', background: c }} />
                            ))}
                        </div>

                        {/* ── Wrapper con fades — desktop ── */}
                        <div
                            className="nav-desktop-wrap"
                            style={{ position: 'relative', flex: 1, minWidth: 0 }}
                        >
                            {/* Fade izquierda — pointer-events:none para no bloquear el scroll */}
                            <div style={{
                                position: 'absolute', left: 0, top: 0, bottom: 0, width: '56px',
                                background: `linear-gradient(to right, ${isDark ? '#110820' : '#fff'} 55%, transparent)`,
                                pointerEvents: 'none', zIndex: 3,
                                opacity: fadeLeft ? 1 : 0, transition: 'opacity 0.25s',
                            }} />

                            {/* Fade derecha */}
                            <div style={{
                                position: 'absolute', right: 0, top: 0, bottom: 0, width: '56px',
                                background: `linear-gradient(to left, ${isDark ? '#110820' : '#fff'} 55%, transparent)`,
                                pointerEvents: 'none', zIndex: 3,
                                opacity: fadeRight ? 1 : 0, transition: 'opacity 0.25s',
                            }} />

                            {/*
                              ── Scroll container ──
                              • overflow-x: auto  → permite rueda de mouse, trackpad, arrastre táctil
                              • overflow-y: visible → los dropdowns del portal no se cortan
                              • scrollbar oculto vía CSS class .adm-scroll
                            */}
                            <div
                                ref={menuRef}
                                className="adm-scroll"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '4px 2px',
                                }}
                            >
                                {navItems.map(item => {
                                    const isOpen = openDropdown === item.id;
                                    const active = isItemActive(item);
                                    return (
                                        <button
                                            key={item.id}
                                            data-dropdown-trigger={item.id}
                                            onClick={e => handleTriggerClick(item.id, e)}
                                            className="nav-trigger"
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '7px',
                                                padding: '7px 14px', borderRadius: '10px', flexShrink: 0,
                                                border: active ? `1.5px solid ${item.color}40` : '1.5px solid transparent',
                                                background: active ? (isDark ? `${item.color}22` : `${item.color}10`) : 'transparent',
                                                fontSize: '0.83rem', fontWeight: '700',
                                                color: active ? item.color : (isDark ? '#e2e8f0' : '#000000'),
                                                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                                whiteSpace: 'nowrap', transition: 'all 0.2s',
                                            }}
                                            onMouseEnter={e => {
                                                if (!active) {
                                                    e.currentTarget.style.background = isDark ? `${item.color}20` : `${item.color}0a`;
                                                    e.currentTarget.style.color = item.color;
                                                    e.currentTarget.style.border = `1.5px solid ${item.color}35`;
                                                    const iconBox = e.currentTarget.querySelector('.nav-icon-box') as HTMLElement | null;
                                                    if (iconBox) {
                                                        iconBox.style.background = item.color;
                                                        iconBox.style.color = '#ffffff';
                                                        iconBox.style.border = `1px solid ${item.color}`;
                                                        iconBox.style.boxShadow = `0 2px 8px ${item.color}40`;
                                                    }
                                                    const chev = e.currentTarget.querySelector('.nav-chevron') as HTMLElement | null;
                                                    if (chev) chev.style.color = item.color;
                                                }
                                            }}
                                            onMouseLeave={e => {
                                                if (!active) {
                                                    e.currentTarget.style.background = 'transparent';
                                                    e.currentTarget.style.color = isDark ? '#e2e8f0' : '#000000';
                                                    e.currentTarget.style.border = '1.5px solid transparent';
                                                    const iconBox = e.currentTarget.querySelector('.nav-icon-box') as HTMLElement | null;
                                                    if (iconBox) {
                                                        iconBox.style.background = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)';
                                                        iconBox.style.color = isDark ? '#e2e8f0' : '#000000';
                                                        iconBox.style.border = isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)';
                                                        iconBox.style.boxShadow = 'none';
                                                    }
                                                    const chev = e.currentTarget.querySelector('.nav-chevron') as HTMLElement | null;
                                                    if (chev) chev.style.color = isDark ? '#cbd5e1' : '#64748b';
                                                }
                                            }}
                                        >
                                            <span
                                                className="nav-icon-box"
                                                style={{
                                                    width: '26px', height: '26px', borderRadius: '7px',
                                                    background: active ? item.color : (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)'),
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    color: active ? '#ffffff' : (isDark ? '#e2e8f0' : '#000000'),
                                                    border: active ? `1px solid ${item.color}` : (isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)'),
                                                    boxShadow: active ? `0 2px 8px ${item.color}40` : 'none',
                                                    transition: 'all 0.2s', flexShrink: 0,
                                                }}
                                            >
                                                {item.icon}
                                            </span>
                                            <span>{item.label}</span>
                                            <ChevronDown
                                                size={13}
                                                className="nav-chevron"
                                                style={{
                                                    color: active ? item.color : (isDark ? '#cbd5e1' : '#64748b'),
                                                    transition: 'transform 0.25s cubic-bezier(.16,1,.3,1), color 0.2s',
                                                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                }}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ── Hamburguesa — solo móvil ── */}
                        <div className="nav-hamburger" style={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                                {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c, i) => (
                                    <div key={i} style={{ width: i === 0 ? '14px' : '5px', height: '3px', borderRadius: '3px', background: c }} />
                                ))}
                            </div>
                            <button
                                onClick={() => { setMobileOpen(o => !o); setMobileExpanded(null); }}
                                style={{
                                    width: '36px', height: '36px', borderRadius: '10px',
                                    border: isDark ? '1.5px solid rgba(193, 52, 139, 0.3)' : '1.5px solid #f1f5f9',
                                    background: isDark ? '#1c1032' : '#f8fafc',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer', transition: 'all .2s',
                                }}
                            >
                                {mobileOpen ? <X size={16} color={isDark ? '#f8fafc' : '#374151'} /> : <Menu size={16} color={isDark ? '#f8fafc' : '#374151'} />}
                            </button>
                        </div>

                    </div>
                </div>

                {/* ── Menú móvil ── */}
                <Transition
                    show={mobileOpen}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 -translate-y-2"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 -translate-y-2"
                >
                    <div className="adm-mobile-scroll" style={{
                        borderTop: isDark ? '1.5px solid rgba(193, 52, 139, 0.3)' : '1.5px solid #f1f5f9',
                        background: isDark ? '#110820' : '#fff',
                        padding: '8px 16px 12px', maxHeight: '70vh', overflowY: 'auto'
                    }}>
                        {navItems.map(item => {
                            const isExpanded = mobileExpanded === item.id;
                            const active = isItemActive(item);
                            return (
                                <div key={item.id}>
                                    <button
                                        onClick={() => setMobileExpanded(isExpanded ? null : item.id)}
                                        style={{
                                            width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                                            padding: '10px 12px', borderRadius: '12px', border: 'none',
                                            background: isExpanded || active ? (isDark ? `${item.color}25` : `${item.color}0c`) : 'transparent',
                                            cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", transition: 'background .15s',
                                        }}
                                    >
                                        <span style={{
                                            width: '28px', height: '28px', borderRadius: '8px',
                                            background: isExpanded || active ? item.color : (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)'),
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: isExpanded || active ? '#fff' : (isDark ? '#e2e8f0' : '#000000'),
                                            border: isExpanded || active ? `1px solid ${item.color}` : (isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)'),
                                            transition: 'all .2s', flexShrink: 0,
                                        }}>
                                            {item.icon}
                                        </span>
                                        <span style={{ fontSize: '.88rem', fontWeight: '700', color: isExpanded || active ? item.color : (isDark ? '#e2e8f0' : '#000000'), flex: 1, textAlign: 'left' }}>
                                            {item.label}
                                        </span>
                                        <ChevronDown size={13} color={isExpanded || active ? item.color : (isDark ? '#cbd5e1' : '#64748b')} style={{ transition: 'transform .25s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }} />
                                    </button>

                                    {mobileExpanded === item.id && (
                                        <div style={{ paddingLeft: '50px', paddingBottom: '4px' }}>
                                            {item.links.map(link => (
                                                <Link key={link.href} to={link.href}
                                                    onClick={() => { setMobileOpen(false); }}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '10px', textDecoration: 'none', color: isDark ? '#cbd5e1' : '#374151', transition: 'background .15s' }}
                                                    onMouseEnter={e => { e.currentTarget.style.background = isDark ? `${item.color}25` : `${item.color}08`; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                                >
                                                    {link.icon ? (
                                                        <span style={{ color: item.color, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                            {link.icon}
                                                        </span>
                                                    ) : (
                                                        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                                                    )}
                                                    <div>
                                                        <div style={{ fontSize: '.83rem', fontWeight: '700', color: isDark ? '#f8fafc' : '#0f172a' }}>{link.label}</div>
                                                        <div style={{ fontSize: '.7rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '500' }}>{link.desc}</div>
                                                    </div>
                                                    <div style={{ marginLeft: 'auto', fontSize: '.75rem', color: `${item.color}60` }}>→</div>
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Transition>
            </nav>

            {/* ── Dropdown portal — FUERA del nav ── */}
            {activeItem && triggerRect && (
                <Transition
                    show={!!openDropdown}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                >
                    <DropdownMenu
                        item={activeItem}
                        triggerRect={triggerRect}
                        onClose={() => { setOpenDropdown(null); setTriggerRect(null); }}
                    />
                </Transition>
            )}
        </>
    );
};

export default Navbar;

